// Tabblad Uren in de taaklade (ontwerp "Tabblad Uren", https://claude.ai/artifact/6BQ1WnpvGYWYCFEywoEYXg): de urenmeter
// met de schatting, de timer met een omschrijving, de Clockify-taken waarop gelogd wordt (met klant en project), en per
// dag wie wat logde, het nieuwste boven.
// Koppelingen zijn n-op-n (Rens, 23/10): een taak kan meerdere Clockify-taken hebben (de meter telt ze samen, de timer
// laat kiezen) en een Clockify-taak kan bij meerdere taken horen (haar uren tellen hier volledig mee, met wie ze deelt).
// Rechten: de timer start iedereen voor zichzelf (als die in Clockify staat), enkel op een Clockify-taak en met een
// omschrijving. De schatting, een Clockify-taak maken (met een eigen naam), een bestaande koppelen, de naam wijzigen en
// bewust zonder: de planner en de eigenaar. Een nieuwe klant of een nieuw project in Clockify, en een klantplan zonder
// project koppelen: enkel de planner. De omschrijving van uw eigen registraties vult u hier achteraf aan.
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import {
  clockifyApi, fmtHours, fmtSince, useClockifyLive, useClockifyWrite, useTaskLog, useTimer,
  type ClockifyChoice, type ClockifyClient, type ClockifyLive, type ClockifyTaskHours, type NewClockifyTask, type TaskHours as Hours, type TaskLog,
} from "../lib/api-clockify";
import { faseProjectOf } from "../lib/fases";
import { useIndex, usePlanning } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { addDays } from "../lib/planning";
import type { Me, Task } from "../lib/types";
import { useToast } from "../shell/Toast";
import { Av } from "./Avatar";
import { clockifyReady, timerOnTask, useNow } from "./TaskCard";

const EST_PICKS = [4, 8, 16, 24, 40];
const EST_ADD = [4, 8, 16];
const MAX_TEXT = 500; // naam en omschrijving, zoals de server ze bewaart
const DAYS = ["zondag", "maandag", "dinsdag", "woensdag", "donderdag", "vrijdag", "zaterdag"];
const n1 = (h: number) => String(Math.round(h * 100) / 100).replace(".", ",");
const dm = (d: string) => d.slice(8, 10) + "/" + d.slice(5, 7);
const note = (text: string) => <p className="p-kd-note" style={{ margin: 0 }}>{text}</p>;
/** lopende tijd als 0:18:04 */
const clockOf = (since: string, now: number) => {
  const s = Math.max(0, Math.floor((now - Date.parse(since)) / 1e3));
  return Math.floor(s / 3600) + ":" + String(Math.floor(s / 60) % 60).padStart(2, "0") + ":" + String(s % 60).padStart(2, "0");
};
// tikt elke seconde, zonder het hele tabblad opnieuw te tekenen
function Elapsed({ since }: { since: string }) {
  const now = useNow(1e3);
  return <>{clockOf(since, now)}</>;
}

/** De Clockify-taken van een taak die nog in Clockify bestaan. */
const cksOf = (h: Hours | undefined): ClockifyTaskHours[] => (h?.tasks || []).filter((c) => !c.missing);

/** De uren van deze taak in de gedeelde live-gegevens zetten (het antwoord van de server), of weghalen. */
function useSetHours(taskId: string) {
  const qc = useQueryClient();
  return (hours: Hours | null | undefined) => qc.setQueryData<ClockifyLive>(["clockify-live"], (old) => {
    if (!old) return old;
    const tasks = { ...old.tasks };
    if (hours) tasks[taskId] = hours; else delete tasks[taskId];
    return { ...old, tasks };
  });
}

// Schatting in uren, met komma of punt; bewaren bij Enter of als het veld de focus verliest, Escape laat ze staan.
// Leegmaken wist de schatting. onDone: na elke poging, ook zonder wijziging (het potlood sluit dan het veld).
function EstInput({ value, busy, onSave, small, autoFocus, onDone }: { value: number | null; busy: boolean; onSave: (h: number) => void; small?: boolean; autoFocus?: boolean; onDone?: () => void }) {
  const toast = useToast();
  const [draft, setDraft] = useState<string | null>(null);
  const cancel = useRef(false);
  const commit = () => {
    const d = draft, skip = cancel.current;
    setDraft(null);
    cancel.current = false;
    onDone?.();
    if (skip || d == null) return;
    const n = d.trim() === "" ? 0 : Number(d.trim().replace(",", "."));
    if (!Number.isFinite(n) || n < 0 || n > 2000) { toast.flash("Een schatting ligt tussen 0 en 2000 uur", { error: true }); return; }
    const h = Math.round(n * 100) / 100;
    if (h !== (value ?? 0)) onSave(h);
  };
  return (
    <input className={"ex-input p-ck-est" + (small ? " s" : "")} type="text" inputMode="decimal" placeholder={small ? "andere" : "uren"} aria-label="Schatting in uren"
      disabled={busy} autoFocus={autoFocus} value={draft ?? (value == null ? "" : n1(value))}
      onChange={(e) => setDraft(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") { cancel.current = true; e.currentTarget.blur(); } }} />
  );
}

export function TaskHours({ task, me, klant, today, manage, planner, owners, firstOf }: {
  task: Task; me: Me;
  /** naam van het klantplan */
  klant: string;
  today: string;
  /** planner of eigenaar: schatting en de Clockify-taak */
  manage: boolean;
  planner: boolean;
  owners: string;
  firstOf: (pid: string) => string;
}) {
  const { data: live, isError } = useClockifyLive();
  const now = useNow();
  // een Clockify-taak toevoegen, of toch een na bewust zonder
  const [choose, setChoose] = useState(false);
  if (!live) return note(isError ? "De uren uit Clockify zijn nu niet te lezen." : "Uren uit Clockify laden…");
  const st = live.clockify;
  if (!clockifyReady(st)) {
    return note(st.state === "uit" ? "Clockify is nog niet gekoppeld. Schatting, gelogde uren en de timer komen hier zodra dat zo is."
      : st.state === "fout" ? "Clockify antwoordt nu niet; de uren verschijnen zodra de koppeling weer werkt."
      : "Clockify is gekoppeld; de eerste uren verschijnen binnen enkele minuten.");
  }

  const h: Hours | undefined = live.tasks[task.id];
  const linked = !!h && !h.none && !h.missing;
  const cks = cksOf(h);
  // uw eigen timer op deze taak staat al bij de timer; Nu bezig toont dan de anderen
  const here = live.timers.filter((t) => timerOnTask(t, task) && !(linked && t.personId === me.id && !t.stale));
  const nuBezig = here.length > 0 && (
    <div className="p-hrow" style={{ marginTop: 0 }}><span className="ex-label">Nu bezig</span>
      <div className="p-quick">
        {here.map((t) => (
          <span key={t.personId} className={"p-task-live" + (t.stale ? " t-warn" : "")} title={"Sinds " + new Date(t.since).toLocaleString("nl-BE", { weekday: "short", hour: "2-digit", minute: "2-digit" })}>
            <span className={"ex-dot ex-dot--" + (t.stale ? "warn" : "ok")} />{t.personId === me.id ? "U" : firstOf(t.personId)} · {fmtSince(t.since, now)}{t.stale ? " · wellicht vergeten" : ""}
          </span>
        ))}
      </div>
    </div>
  );

  if (!linked) {
    if (h?.none && !choose) {
      return (
        <div className="p-uren">
          <div className="p-ck-off">
            <p><b>Bewust zonder Clockify-taak.</b> De uren van deze taak volgt de app niet op.</p>
            {manage && <p style={{ margin: 0 }}><button type="button" className="ex-link" onClick={() => setChoose(true)}>Toch een Clockify-taak</button></p>}
          </div>
          {nuBezig}
        </div>
      );
    }
    return (
      <div className="p-uren">
        {manage ? <Chooser task={task} klant={klant} planner={planner} live={live} h={h} again={choose} onDone={() => setChoose(false)} />
          : note((h?.missing ? "De Clockify-taak van deze taak bestaat niet meer. " : "Nog geen Clockify-taak: zonder Clockify-taak logt de app geen tijd. ")
            + "Die maakt " + (owners || "de eigenaar") + " of de planner.")}
        {nuBezig}
      </div>
    );
  }

  return (
    <div className="p-uren">
      <Meter task={task} h={h} live={live} manage={manage} owners={owners} />
      <TimerBox task={task} me={me} live={live} h={h} />
      {nuBezig}
      {cks.map((c) => <TaskBox key={c.clockifyTaskId} task={task} c={c} several={cks.length > 1} manage={manage} />)}
      {manage && (choose
        ? <Chooser task={task} klant={klant} planner={planner} live={live} h={h} again onDone={() => setChoose(false)} />
        : <div><button type="button" className="ex-link p-icl" onClick={() => setChoose(true)}><Icon name="plus" size={13} />Clockify-taak toevoegen</button></div>)}
      <LogList task={task} today={today} several={cks.length > 1} />
    </div>
  );
}

/**
 * De urenmeter: gelogd tegenover de schatting, met de lopende timers gestreept. Zonder schatting vraagt de meter ernaar;
 * elke wijziging van de schatting kan ongedaan.
 */
function Meter({ task, h, live, manage, owners }: { task: Task; h: Hours; live: ClockifyLive; manage: boolean; owners: string }) {
  const cw = useClockifyWrite();
  const toast = useToast();
  const setHours = useSetHours(task.id);
  const now = useNow();
  const [editing, setEditing] = useState(false); // het potlood: de schatting wijzigen
  // met meerdere Clockify-taken zet u de schatting per Clockify-taak, in haar kaart
  const cks = cksOf(h), one = cks.length === 1 ? cks[0] : null;
  const canEst = manage && !!one;
  // schatting zetten, met Ongedaan maken (0 wist ze weer)
  const save = (hours: number, undo = true) => {
    const before = h.estimate ?? null;
    cw.run(() => clockifyApi.estimate(task.id, hours, one?.clockifyTaskId)).then((r) => {
      setHours(r.hours);
      if (undo && r.ok) toast.flash(r.text, { undo: () => save(before ?? 0, false) });
    }).catch(() => {});
  };
  const est = h.estimate ?? null, logged = h.logged || 0;
  const over = est != null && logged > est;
  // de lopende timers (niet de vergeten) tellen gestreept mee in de balk
  const runs = live.timers.filter((t) => timerOnTask(t, task) && !t.stale);
  const runH = runs.reduce((a, t) => a + Math.max(0, now - Date.parse(t.since)) / 36e5, 0);
  const scale = Math.max(est ?? 0, logged + runH, 0.01);
  const pct = (x: number) => (Math.min(100, (x / scale) * 100)).toFixed(1) + "%";
  return (
    <div className={"p-ckm" + (over ? " over" : "")}>
      <div className="p-ckm-top">
        <div className="p-ckm-big"><b>{fmtHours(logged)}</b><span>gelogd</span>
          {runs.length > 0 && <span className="p-ckm-run"><span className="ex-dot ex-dot--ok" />+ {runs.length === 1 ? "1 timer loopt" : runs.length + " timers lopen"}</span>}
        </div>
        <div className="p-ckm-r">
          {est == null ? <span className="p-ckm-est">Geen schatting</span>
            : editing ? <span className="p-ckm-est">Schatting <EstInput value={est} busy={cw.busy} onSave={save} autoFocus onDone={() => setEditing(false)} /> u</span>
            : <span className="p-ckm-est">van <b>{fmtHours(est)}</b> geschat
              {canEst && <button type="button" className="p-ckm-pen" aria-label="Schatting wijzigen" title="Schatting wijzigen" onClick={() => setEditing(true)}><Icon name="pen" size={15} /></button>}
            </span>}
        </div>
      </div>
      {est != null && <>
        <div className="p-ckm-bar" role="img" aria-label={n1(logged) + " van " + n1(est) + " u gelogd"}>
          <i style={{ width: pct(logged) }} />
          {runH > 0 && <i className="run" style={{ width: "max(3px, " + pct(runH) + ")" }} />}
          {over && <em style={{ left: pct(est) }} title={"Schatting " + fmtHours(est)} />}
        </div>
        <div className="p-ckm-sub">{over ? <b className="t-err">{fmtHours(logged - est)} over de schatting</b> : <><b>{fmtHours(h.rest ?? Math.max(0, est - logged))}</b> te gaan</>}</div>
        {over && canEst && (
          <div className="p-hrow"><span className="ex-label">Schatting bijstellen</span>
            <div className="p-quick">{EST_ADD.map((x) => <button key={x} type="button" className="p-tog" disabled={cw.busy} onClick={() => save(est + x)}>+ {x} u</button>)}</div>
          </div>
        )}
      </>}
      {est == null && (manage && !one ? note("Zet de schatting per Clockify-taak, hieronder.")
        : manage
        ? <div className="p-ckm-ask"><span className="ex-label">Hoeveel uur vraagt deze taak?</span>
            <div className="p-quick">
              {EST_PICKS.map((x) => <button key={x} type="button" className="p-tog" disabled={cw.busy} onClick={() => save(x)}>{x} u</button>)}
              <EstInput value={null} busy={cw.busy} onSave={save} small />
            </div>
            {note("Zonder schatting telt deze taak niet mee in de bezetting" + (owners ? " van " + owners : "") + " op Capaciteit.")}
          </div>
        : note("Nog geen schatting. Die zet " + (owners || "de eigenaar") + " of de planner."))}
    </div>
  );
}

/**
 * De timer voor uzelf. Hij start enkel met een omschrijving (waaraan werkt u?); terwijl hij loopt staat ze erbij en
 * wijzigt u ze, ze gaat zo naar Clockify. Een timer die in Clockify zelf zonder omschrijving startte, vult u hier aan.
 * Loopt uw timer op een andere taak, dan brengt Timer hierheen hem hier.
 */
function TimerBox({ task, me, live, h }: { task: Task; me: Me; live: ClockifyLive; h: Hours }) {
  const timer = useTimer();
  const now = useNow();
  const [desc, setDesc] = useState("");
  const [edit, setEdit] = useState<string | null>(null); // de omschrijving van de lopende timer wijzigen
  const cks = cksOf(h);
  const [on, setOn] = useState(""); // op welke Clockify-taak, als er meerdere zijn
  const onId = cks.some((c) => c.clockifyTaskId === on) ? on : cks[0]?.clockifyTaskId || "";
  const mine = live.timers.find((t) => t.personId === me.id);
  if (!live.linked.includes(me.id)) return note("U staat (nog) niet in Clockify met uw TLCC-adres, dus de timer start u hier niet.");
  if (mine?.stale) {
    return (
      <p className="p-ck-else warn" style={{ margin: 0 }}><Icon name="clock" size={14} /><span>Uw timer loopt al sinds {new Date(mine.since).toLocaleString("nl-BE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}, wellicht vergeten. Stop of corrigeer hem in Clockify zelf; de app boekt die uren niet.</span></p>
    );
  }

  if (mine && timerOnTask(mine, task)) {
    const editing = edit != null || !mine.description;
    const val = edit ?? "";
    const save = () => { const v = val.trim(); if (v && !timer.busy) timer.describe(v).then(() => setEdit(null)).catch(() => {}); };
    return (
      <div className="p-tmr run">
        <div className="p-tmr-row">
          <button type="button" className="ex-btn p-ck-on" disabled={timer.busy} onClick={timer.stop}><Icon name="stop" size={14} />Stoppen · <Elapsed since={mine.since} /></button>
          {editing ? <span className="p-tmr-desc"><small>loopt op {mine.taskName || h.name}{mine.description ? "" : ", nog zonder omschrijving"}</small></span>
            : <>
              <span className="p-tmr-desc">{mine.description}<small>loopt op {mine.taskName || h.name}</small></span>
              <button type="button" className="ex-link p-icl" onClick={() => setEdit(mine.description)}><Icon name="pen" size={13} />Wijzigen</button>
            </>}
        </div>
        {editing && (
          <div className="p-tmr-row">
            <input className="ex-input" aria-label="Waaraan werkt u?" placeholder="Waaraan werk je?" maxLength={MAX_TEXT} autoFocus={edit != null} value={val}
              onChange={(e) => setEdit(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape" && mine.description) setEdit(null); }} />
            <button type="button" className="ex-btn ex-btn--primary" disabled={!val.trim() || timer.busy} onClick={save}>Bewaren</button>
            {mine.description && <button type="button" className="ex-btn" onClick={() => setEdit(null)}>Annuleren</button>}
          </div>
        )}
      </div>
    );
  }

  const where = mine && (mine.taskName || (mine.project ? "het project " + mine.project + (mine.client ? " van " + mine.client : "") : "") || mine.description || "een andere taak");
  const start = () => { const d = desc.trim(); if (d && !timer.busy) timer.start(task.id, task.title, d, onId || undefined).then(() => setDesc("")).catch(() => {}); };
  return (
    <div className="p-tmr">
      <label className="ex-label" htmlFor={"tmr-" + task.id}>Waaraan werk je?</label>
      <div className="p-tmr-row">
        <input id={"tmr-" + task.id} className="ex-input" maxLength={MAX_TEXT} placeholder="Bijvoorbeeld: statusmapping testen met de klant" value={desc}
          onChange={(e) => setDesc(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") start(); }} />
        {cks.length > 1 && (
          <select className="ex-input p-tmr-on" aria-label="Op welke Clockify-taak" value={onId} onChange={(e) => setOn(e.target.value)}>
            {cks.map((c) => <option key={c.clockifyTaskId} value={c.clockifyTaskId}>op {c.name}</option>)}
          </select>
        )}
        <button type="button" className="ex-btn ex-btn--primary" disabled={!desc.trim() || timer.busy} onClick={start}
          title={mine ? "Uw timer loopt nu op " + where + " (" + fmtSince(mine.since, now) + "); hierheen stopt hem daar en start hier" : undefined}>
          <Icon name={mine ? "right" : "play"} size={14} />{mine ? "Timer hierheen" : "Timer starten"}
        </button>
      </div>
    </div>
  );
}

/**
 * Een gekoppelde Clockify-taak: de naam (te wijzigen, met Ongedaan maken), klant en project, met welke andere taken ze
 * gedeeld is, en Loskoppelen (met Ongedaan maken). Met meerdere Clockify-taken staan hier ook haar schatting en uren.
 */
function TaskBox({ task, c, several, manage }: { task: Task; c: ClockifyTaskHours; several: boolean; manage: boolean }) {
  const cw = useClockifyWrite();
  const toast = useToast();
  const setHours = useSetHours(task.id);
  const [name, setName] = useState<string | null>(null);
  const [est, setEst] = useState(false);
  const id = c.clockifyTaskId;
  const rename = (to: string, undo = true) => {
    const v = to.trim();
    if (!v || v === c.name) { setName(null); return; }
    cw.run(() => clockifyApi.rename(task.id, v, id)).then((r) => {
      setHours(r.hours);
      setName(null);
      if (undo && r.changeId) toast.flash(r.text, { undo: () => rename(r.before, false) });
    }).catch(() => {});
  };
  const link = (mode: "add" | "remove", undo = true) => {
    cw.run(() => clockifyApi.linkTasks([{ taskId: task.id, clockifyTaskId: id, mode }])).then((r) => {
      setHours(r.hours ?? null);
      if (undo) toast.flash(r.text, { undo: () => link(mode === "add" ? "remove" : "add", false) });
    }).catch(() => {});
  };
  const estimate = (hours: number, undo = true) => {
    const before = c.estimate ?? 0;
    cw.run(() => clockifyApi.estimate(task.id, hours, id)).then((r) => {
      setHours(r.hours);
      if (undo && r.ok) toast.flash(r.text, { undo: () => estimate(before, false) });
    }).catch(() => {});
  };
  return (
    <div className="p-ckt">
      <div className="p-ckt-h"><span className="p-ckt-l">Clockify-taak</span>
        {manage && (
          <span className="p-ckt-acts">
            {name == null && <button type="button" className="ex-link p-icl" onClick={() => setName(c.name || "")}><Icon name="pen" size={13} />Naam wijzigen</button>}
            <button type="button" className="ex-link p-icl" disabled={cw.busy} onClick={() => link("remove")}><Icon name="x" size={13} />Loskoppelen</button>
          </span>
        )}
      </div>
      {name != null ? <>
        <div className="p-ckt-edit">
          <input className="ex-input" aria-label="Taak in Clockify" maxLength={MAX_TEXT} autoFocus value={name} onChange={(e) => setName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") rename(name); if (e.key === "Escape") setName(null); }} />
          <button type="button" className="ex-btn ex-btn--primary" disabled={!name.trim() || cw.busy} onClick={() => rename(name)}>Bewaren</button>
          <button type="button" className="ex-btn" onClick={() => setName(null)}>Annuleren</button>
        </div>
        {note("De naam verandert in Clockify zelf, voor iedereen die erop logt. Planner houdt zijn titel.")}
      </> : <div className="p-ckt-name">{c.name}{c.done && <span className="p-ckm-done">Afgerond in Clockify</span>}</div>}
      <div className="p-ckt-cards">
        <div className="p-ckt-card"><span>Klant</span><b>{c.client || "Zonder klant"}</b></div>
        <div className="p-ckt-card"><span>Project</span><b>{c.project || "–"}</b></div>
      </div>
      {several && (
        <div className="p-ckt-est">
          <span>{fmtHours(c.logged || 0)} gelogd</span>
          {est ? <span>Schatting <EstInput value={c.estimate ?? null} busy={cw.busy} onSave={estimate} autoFocus onDone={() => setEst(false)} /> u</span>
            : <span>{c.estimate ? <>van <b>{fmtHours(c.estimate)}</b> geschat</> : "geen schatting"}
              {manage && <button type="button" className="p-ckm-pen" aria-label="Schatting wijzigen" title="Schatting wijzigen" onClick={() => setEst(true)}><Icon name="pen" size={14} /></button>}
            </span>}
        </div>
      )}
      {c.shared.length > 0 && <p className="p-ckt-shared">Ook bij {c.shared.map((x) => x.title).join(", ")}: de uren tellen bij elke taak, in de bezetting één keer.</p>}
    </div>
  );
}

type Sel = { id: string; name: string; isNew: boolean };
type Mode = "nieuw" | "bestaand" | "zonder";
const sameClient = (a: Sel | null, b: { id: string; name: string }) => !!a && !a.isNew && a.id === b.id && a.name === b.name;

/**
 * Nog geen Clockify-taak, of er een toevoegen: een nieuwe maken met een eigen naam (de titel uit Planner staat al
 * ingevuld), een bestaande koppelen (ook een die al bij een andere taak hoort), of bewust zonder. Klant en project zijn
 * kaartjes om te kiezen of (planner) te maken; standaard het project van de eerste Clockify-taak, anders dat van het
 * klantplan.
 */
function Chooser({ task, klant, planner, live, h, again, onDone }: {
  task: Task; klant: string; planner: boolean; live: ClockifyLive; h: Hours | undefined;
  /** Clockify-taak toevoegen, of Toch een Clockify-taak na bewust zonder: met Annuleren */
  again: boolean;
  onDone: () => void;
}) {
  const cw = useClockifyWrite();
  const toast = useToast();
  const setHours = useSetHours(task.id);
  const plan = live.plans[task.projectId];
  // het Clockify-project van een fase erboven gaat voor op dat van het klantplan (Rens, 26/10)
  const { data: pdata } = usePlanning();
  const pidx = useIndex(pdata);
  const faseProj = faseProjectOf(task.faseId, pidx.fases, live.faseProjects);
  const current = h && !h.none && !h.missing ? h : null;
  const [mode, setMode] = useState<Mode>(current ? "bestaand" : "nieuw");
  const [name, setName] = useState(task.title);
  const [est, setEst] = useState<number | null>(null);
  const [q, setQ] = useState("");
  const [sel, setSel] = useState<{ client: Sel | null; project: Sel | null } | null>(null);
  const [linkPlan, setLinkPlan] = useState(true);
  const { data: cl, isError: clErr } = useQuery({ queryKey: ["clockify-klanten"], queryFn: clockifyApi.clients, staleTime: 5 * 60e3 });
  const clients = cl?.clients || [];

  const defPid = current?.projectId || faseProj?.project.clockifyProjectId || plan?.clockifyProjectId || "";
  const def = (() => {
    const c = clients.find((x) => x.projects.some((p) => p.id === defPid)), p = c?.projects.find((x) => x.id === defPid);
    if (c && p) return { client: { id: c.id, name: c.name, isNew: false }, project: { id: p.id, name: p.name, isNew: false } };
    if (defPid && !cl) {  // de klanten laden nog (of niet): het project van de fase of het klantplan staat al klaar
      const src = current || faseProj?.project || plan;
      const pn = src?.name || "", cn = (src && "client" in src ? src.client : "") || "";
      return { client: { id: "", name: cn, isNew: false }, project: { id: defPid, name: pn, isNew: false } };
    }
    return { client: null, project: null };
  })();
  const { client, project } = sel ?? def;
  const pid = project && !project.isNew ? project.id : "";
  const { data: ch } = useQuery({ queryKey: ["clockify-keuzes", task.id, pid], queryFn: () => clockifyApi.choices(task.id, pid), enabled: !!pid, staleTime: 30e3 });
  const choices: ClockifyChoice[] = ch?.tasks || [];
  const key = (s: string) => s.trim().toLowerCase();
  const exists = mode === "nieuw" && name.trim() ? choices.find((c) => key(c.name) === key(name)) : undefined;
  const planLinkable = planner && !plan && !!project;
  // met Clockify-taken komt er een bij; anders vervangt de keuze bewust zonder of een verdwenen Clockify-taak
  const prev = h?.none ? "" : h?.missing ? h.clockifyTaskId : null; // voor Ongedaan maken

  const back = (to: { clockifyTaskId: string | null; mode?: "set" | "remove" }) => () => {
    cw.run(() => clockifyApi.linkTasks([{ taskId: task.id, ...to }])).then((r) => setHours(r.hours ?? null)).catch(() => {});
  };
  const how = (): NewClockifyTask => {
    const lp = planLinkable && linkPlan;
    if (!project!.isNew) return { name: name.trim(), projectId: project!.id, linkPlan: lp };
    return { name: name.trim(), newProject: project!.name, ...(client!.isNew ? { newClient: client!.name } : { clientId: client!.id }), linkPlan: lp };
  };
  // de server zet een nieuwe Clockify-taak naast wat er al hangt
  const make = () => {
    cw.run(() => clockifyApi.createTask(task.id, est ?? undefined, how())).then((r) => { setHours(r.hours ?? null); onDone(); }).catch(() => {});
  };
  const link = (c: ClockifyChoice) => {
    const mode = current ? "add" : "set";
    cw.run(() => clockifyApi.linkTasks([{ taskId: task.id, clockifyTaskId: c.clockifyTaskId, mode }], planLinkable && linkPlan)).then((r) => {
      setHours(r.hours ?? null);
      onDone();
      toast.flash(r.text, { undo: back(current ? { clockifyTaskId: c.clockifyTaskId, mode: "remove" } : { clockifyTaskId: prev }) });
    }).catch(() => {});
  };
  const none = () => {
    cw.run(() => clockifyApi.linkTasks([{ taskId: task.id, clockifyTaskId: "" }])).then((r) => {
      setHours(r.hours ?? null);
      onDone();
      toast.flash(r.text, { undo: back({ clockifyTaskId: prev }) });
    }).catch(() => {});
  };

  const picker = <ClientProjectPick clients={clients} loading={!cl && !clErr} failed={clErr} client={client} project={project} planPid={faseProj?.project.clockifyProjectId || plan?.clockifyProjectId || ""} planner={planner}
    onPick={(c, p) => setSel({ client: c, project: p })} />;
  const planBox = planLinkable && (
    <label className="p-chk"><input type="checkbox" checked={linkPlan} onChange={(e) => setLinkPlan(e.target.checked)} />Ook het klantplan {klant || "van deze taak"} aan dit project koppelen</label>
  );
  const ql = key(q);
  const list = choices.filter((c) => !ql || c.name.toLowerCase().includes(ql));
  const modes: [Mode, string][] = [["nieuw", "Nieuwe maken"], ["bestaand", "Bestaande koppelen"], ...(h?.none || current ? [] : [["zonder", "Bewust zonder"]] as [Mode, string][])];

  return (
    <div className={"p-ckn" + (again ? " relink" : "")}>
      <div className="p-ckn-h">
        <b>{current ? "Clockify-taak toevoegen" : h?.missing ? "De Clockify-taak bestaat niet meer" : "Nog geen Clockify-taak"}</b>
        {again && <button type="button" className="ex-btn" onClick={onDone}>Annuleren</button>}
      </div>
      <div className="ex-seg" role="group" aria-label="Kies">
        {modes.map(([k, t]) => <button key={k} type="button" aria-pressed={mode === k} onClick={() => setMode(k)}>{t}</button>)}
      </div>

      {mode === "nieuw" && <>
        <div className="p-field">
          <label className="ex-label" htmlFor={"ckn-" + task.id}>Taak in Clockify</label>
          <input id={"ckn-" + task.id} className="ex-input" maxLength={MAX_TEXT} value={name} onChange={(e) => setName(e.target.value)} />
          {exists && (
            <p className="p-ck-err">"{exists.name}" bestaat al in {project?.name}{exists.linked ? " en hangt al aan deze taak. Kies een andere naam." : <>. <button type="button" className="ex-link" onClick={() => { setMode("bestaand"); setQ(exists.name); }}>Die koppelen</button> of kies een andere naam.</>}</p>
          )}
        </div>
        {picker}
        {planBox}
        <div className="p-hrow"><span className="ex-label">Schatting</span>
          <div className="p-quick">
            {EST_PICKS.map((x) => <button key={x} type="button" className={"p-tog" + (est === x ? " on" : "")} aria-pressed={est === x} disabled={cw.busy} onClick={() => setEst(est === x ? null : x)}>{x} u</button>)}
            <EstInput value={est != null && !EST_PICKS.includes(est) ? est : null} busy={cw.busy} onSave={(x) => setEst(x || null)} small />
          </div>
          <span className="t-mute" style={{ fontSize: 13 }}>mag ook later</span>
        </div>
        <div className="p-hrow">
          <button type="button" className="ex-btn ex-btn--primary" disabled={!name.trim() || !!exists || !client || !project || cw.busy} onClick={make}>
            <Icon name="plus" size={14} />Clockify-taak maken{est ? " met " + n1(est) + " u" : ""}
          </button>
        </div>
      </>}

      {mode === "bestaand" && <>
        {picker}
        {project && !project.isNew && planBox}
        <label className="p-field"><span className="ex-label">Zoek een Clockify-taak</span>
          <input className="ex-input" placeholder="Naam van de Clockify-taak" value={q} disabled={!pid} onChange={(e) => setQ(e.target.value)} />
        </label>
        <div className="p-cands">
          {list.map((c) => (
            <button key={c.clockifyTaskId} type="button" className="p-cnd" disabled={c.linked || cw.busy} onClick={() => link(c)}>
              <span>{c.name}{c.suggest && <span className="p-sugg">voorstel</span>}
                <small>{c.linked ? "hangt al aan deze taak" : [c.used ? "ook bij " + c.used : "", c.estimate ? "schatting " + fmtHours(c.estimate) + " · " + fmtHours(c.logged) + " gelogd" : ""].filter(Boolean).join(" · ") || "open in Clockify"}</small>
              </span>
              <span className="p-cnd-go">{c.linked ? "gekoppeld" : "Koppelen"}</span>
            </button>
          ))}
          {!list.length && note(!project ? "Kies eerst een klant en een project." : project.isNew ? "Een nieuw project heeft nog geen taken. Maak een nieuwe."
            : !ch ? "Taken laden…" : ql ? "Niets gevonden in dit project. Maak een nieuwe." : "Nog geen open taken in dit project. Maak een nieuwe.")}
        </div>
      </>}

      {mode === "zonder" && <>
        {note("De app volgt de uren van deze taak dan niet op: geen meter, geen timer. Wijzigen kan later in dit kader.")}
        <div className="p-hrow"><button type="button" className="ex-btn" disabled={cw.busy} onClick={none}>Bewust zonder Clockify-taak</button></div>
      </>}
    </div>
  );
}

/**
 * Klant en project als kaartjes om op te klikken: zoeken in Clockify, of (planner) een nieuwe naam typen om er een te
 * maken; die maakt de app samen met de taak aan. Het project van het klantplan staat aangeduid.
 */
function ClientProjectPick({ clients, loading, failed, client, project, planPid, planner, onPick }: {
  clients: ClockifyClient[]; loading: boolean; failed: boolean; client: Sel | null; project: Sel | null; planPid: string; planner: boolean;
  onPick: (client: Sel | null, project: Sel | null) => void;
}) {
  const [open, setOpen] = useState<"klant" | "project" | null>(null);
  const [pq, setPq] = useState("");
  const isK = open === "klant";
  const label = (n: string) => n || "Zonder klant";
  const cObj = client ? clients.find((c) => sameClient(client, c)) : undefined;
  const all = isK ? clients.map((c) => ({ id: c.id, name: c.name })) : cObj?.projects || [];
  const typed = pq.trim(), ql = typed.toLowerCase();
  const list = all.filter((x) => !ql || (isK ? label(x.name) : x.name).toLowerCase().includes(ql));
  const exact = all.some((x) => x.name.toLowerCase() === ql);
  // een nieuw project kan bij een nieuwe klant, een open klant in Clockify, of zonder klant
  const canNew = planner && (isK || (!!client && (client.isNew || !!client.id || !client.name)));
  const offerNew = !!typed && !exact && canNew;

  const show = (w: "klant" | "project") => { setOpen(open === w ? null : w); setPq(""); };
  const pickClient = (c: Sel) => {
    setPq("");
    if (!c.isNew && sameClient(client, c)) { setOpen(project ? null : "project"); return; }
    const first = c.isNew ? undefined : clients.find((x) => sameClient(c, x))?.projects[0];
    onPick(c, first ? { id: first.id, name: first.name, isNew: false } : null);
    setOpen(first ? null : "project");
  };
  const pickProject = (p: Sel) => { onPick(client, p); setOpen(null); setPq(""); };
  const choose = (x: { id: string; name: string }) => (isK ? pickClient({ ...x, isNew: false }) : pickProject({ ...x, isNew: false }));
  const makeNew = () => (isK ? pickClient({ id: "", name: typed, isNew: true }) : pickProject({ id: "", name: typed, isNew: true }));

  const card = (w: "klant" | "project", title: string, v: Sel | null) => (
    <button type="button" className="p-ckt-card" aria-expanded={open === w} disabled={w === "project" && !client} onClick={() => show(w)}>
      <span>{title}</span>
      <b>{v ? (w === "klant" ? label(v.name) : v.name) : <em>Kies {w === "klant" ? "een klant" : "een project"}</em>}</b>
      {v?.isNew && <small>wordt aangemaakt in Clockify</small>}
      <i className="p-chev"><Icon name="down" size={14} /></i>
    </button>
  );
  return (
    <>
      <div className="p-ckt-cards">{card("klant", "Klant", client)}{card("project", "Project", project)}</div>
      {open && (
        <div className="p-pk">
          <input className="ex-input" autoFocus aria-label={isK ? "Klant" : "Project"} value={pq} onChange={(e) => setPq(e.target.value)}
            placeholder={isK ? (planner ? "Zoek of typ een nieuwe klant" : "Zoek een klant") : canNew ? "Zoek of typ een nieuw project" + (client?.name ? " bij " + client.name : "") : "Zoek een project"}
            onKeyDown={(e) => {
              if (e.key === "Escape") { e.preventDefault(); setOpen(null); }
              if (e.key === "Enter") { e.preventDefault(); if (list[0]) choose(list[0]); else if (offerNew) makeNew(); }
            }} />
          <div className="p-pk-list">
            {list.map((x) => {
              const on = isK ? sameClient(client, x) : !!project && !project.isNew && project.id === x.id;
              return (
                <button key={x.id + "|" + x.name} type="button" className={"p-pk-i" + (on ? " on" : "")} onClick={() => choose(x)}>
                  {isK ? label(x.name) : x.name}{!isK && x.id === planPid && <span>standaard voor deze taak</span>}
                </button>
              );
            })}
            {offerNew && <button type="button" className="p-pk-i new" onClick={makeNew}><Icon name="plus" size={13} />{isK ? "Nieuwe klant" : "Nieuw project"} "{typed}"</button>}
            {!list.length && !offerNew && (
              <p className="p-empty">{loading ? "Klanten laden…" : failed ? "De klanten en projecten uit Clockify zijn nu niet te lezen." : isK || typed ? "Niets gevonden." : client?.isNew ? "Typ de naam van het eerste project van deze klant."
                : "Nog geen project bij deze klant." + (canNew ? " Typ een naam om er een te maken." : "")}</p>
            )}
          </div>
        </div>
      )}
    </>
  );
}

/** Gelogd per dag op de Clockify-taak: het nieuwste boven, binnen een dag op tijd; uw eigen omschrijvingen vult u aan. */
function LogList({ task, today, several }: { task: Task; today: string; several: boolean }) {
  const { data: log, isError } = useTaskLog(task.id);
  const qc = useQueryClient();
  const toast = useToast();
  const [edit, setEdit] = useState<{ id: string; text: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    const v = edit?.text.trim();
    if (!edit || !v || busy) return;
    setBusy(true);
    try {
      const r = await clockifyApi.describeEntry(edit.id, v);
      qc.setQueryData<TaskLog>(["uren", task.id], (old) => old && {
        ...old, days: old.days.map((d) => ({ ...d, entries: d.entries.map((e) => (e.id === edit.id ? { ...e, description: v } : e)) })),
      });
      toast.flash(r.text);
      setEdit(null);
    } catch (e) {
      toast.flash(e instanceof Error ? e.message : "Niet bewaard", { error: true });
    } finally {
      setBusy(false);
    }
  };
  const dayName = (d: string) => {
    const w = DAYS[new Date(d + "T12:00:00Z").getUTCDay()] + " " + dm(d);
    return d === today ? "Vandaag, " + w : d === addDays(today, -1) ? "Gisteren, " + w : w.charAt(0).toUpperCase() + w.slice(1);
  };
  const missing = log ? log.days.reduce((a, d) => a + d.entries.filter((e) => !e.description).length, 0) : 0;
  return (
    <div className="p-log">
      <div className="p-log-top"><h3>Gelogd per dag</h3>
        {missing > 0 ? <span className="p-log-miss">{missing === 1 ? "1 registratie" : missing + " registraties"} zonder omschrijving</span> : <span>nieuwste boven</span>}
      </div>
      {!log ? note(isError ? "De uren per dag zijn nu niet te lezen." : "Uren per dag laden…")
        : !log.days.length && !log.earlier ? note("Nog niets gelogd op deze Clockify-taak.")
        : <div className="p-log-days" tabIndex={0} aria-label="Gelogd per dag, het nieuwste boven">
          {log.days.map((d) => (
            <div key={d.date} className="p-log-d">
              <div className="p-log-dh"><b>{dayName(d.date)}</b><span>{fmtHours(d.hours, 2)}</span></div>
              {d.entries.map((e) => (
                <div key={e.id} className="p-log-r">
                  <Av id={e.personId} init={e.initials} cls="p-av s" />
                  <span className="p-log-w">{e.name}{e.mine ? " (u)" : ""}
                    <small>{e.from}–{e.to}{several ? " · " + e.taskName : ""} · {e.description || <span className="p-log-miss">geen omschrijving</span>}
                      {e.mine && edit?.id !== e.id && <button type="button" className="ex-link" onClick={() => setEdit({ id: e.id, text: e.description })}>{e.description ? "wijzigen" : "Omschrijving toevoegen"}</button>}
                    </small>
                    {edit?.id === e.id && (
                      <span className="p-log-edit">
                        <input className="ex-input" aria-label="Omschrijving" maxLength={MAX_TEXT} autoFocus value={edit.text} placeholder="Wat deed u?"
                          onChange={(ev) => setEdit({ id: e.id, text: ev.target.value })} onKeyDown={(ev) => { if (ev.key === "Enter") save(); if (ev.key === "Escape") setEdit(null); }} />
                        <button type="button" className="ex-btn ex-btn--primary" disabled={!edit.text.trim() || busy} onClick={save}>Bewaren</button>
                        <button type="button" className="ex-btn" onClick={() => setEdit(null)}>Annuleren</button>
                      </span>
                    )}
                  </span>
                  <span className="p-log-u">{fmtHours(e.hours, 2)}</span>
                </div>
              ))}
            </div>
          ))}
          {log.earlier > 0 && <div className="p-log-more"><span>Eerder{log.since ? ", vóór " + dm(log.since) : ""}</span><b>{fmtHours(log.earlier)}</b></div>}
        </div>}
    </div>
  );
}
